import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, OBSTACLE_HEIGHT, FLOOR_HEIGHT, TILE_COLORS } from '../constants';
import { SparkleParticles, VortexParticles } from '../shared/Particles3D';

interface TeleporterTile3DProps {
  position: [number, number, number];
  entranceDelay?: number;
}

export default function TeleporterTile3D({ position, entranceDelay = 0 }: TeleporterTile3DProps) {
  const groupRef = useRef<THREE.Group>(null);
  const ringRef = useRef<THREE.Mesh>(null);
  const innerRingRef = useRef<THREE.Mesh>(null);
  const elapsed = useRef(0);
  const entered = useRef(false);

  const tileY = FLOOR_HEIGHT + OBSTACLE_HEIGHT / 2;

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    elapsed.current += delta;

    if (!entered.current) {
      const t = Math.max(0, elapsed.current - entranceDelay);
      const progress = Math.min(t * 4, 1);
      groupRef.current.scale.set(progress, progress, progress);
      if (progress >= 1) entered.current = true;
    }

    // Rotate + pulse the ring
    if (ringRef.current) {
      ringRef.current.rotation.y = elapsed.current * 2;
      ringRef.current.rotation.x = Math.sin(elapsed.current) * 0.3;
      const pulse = 1 + Math.sin(elapsed.current * 3) * 0.12;
      ringRef.current.scale.set(pulse, pulse, pulse);
    }
    if (innerRingRef.current) {
      innerRingRef.current.rotation.y = -elapsed.current * 3;
      innerRingRef.current.rotation.z = Math.cos(elapsed.current * 0.7) * 0.4;
    }
  });

  return (
    <group ref={groupRef} position={[position[0], 0, position[2]]} scale={0}>
      {/* Platform base */}
      <RoundedBox
        args={[TILE_SIZE * 0.85, OBSTACLE_HEIGHT * 0.8, TILE_SIZE * 0.85]}
        radius={0.03}
        smoothness={2}
        position={[0, tileY, 0]}
      >
        <meshStandardMaterial
          color={TILE_COLORS.teleporter.front}
          emissive={TILE_COLORS.teleporter.glow}
          emissiveIntensity={0.2}
          metalness={0.15}
          roughness={0.35}
        />
      </RoundedBox>

      {/* Rotating outer torus ring */}
      <mesh ref={ringRef} position={[0, tileY + 0.25, 0]}>
        <torusGeometry args={[TILE_SIZE * 0.25, 0.03, 8, 24]} />
        <meshStandardMaterial
          color={TILE_COLORS.teleporter.top}
          emissive={TILE_COLORS.teleporter.glow}
          emissiveIntensity={0.6}
          metalness={0.35}
          roughness={0.2}
          transparent
          opacity={0.85}
        />
      </mesh>

      {/* Inner counter-rotating ring */}
      <mesh ref={innerRingRef} position={[0, tileY + 0.25, 0]}>
        <torusGeometry args={[TILE_SIZE * 0.15, 0.02, 6, 16]} />
        <meshStandardMaterial
          color={TILE_COLORS.teleporter.glow}
          emissive={TILE_COLORS.teleporter.glow}
          emissiveIntensity={0.8}
          transparent
          opacity={0.65}
        />
      </mesh>

      {/* Vortex spiral particles */}
      <group position={[0, tileY, 0]}>
        <VortexParticles
          count={20}
          radius={TILE_SIZE * 0.3}
          color="#ff00ff"
        />
      </group>

      {/* Sparkle particles */}
      <group position={[0, tileY + 0.2, 0]}>
        <SparkleParticles
          count={12}
          bounds={[TILE_SIZE * 0.6, 1.2, TILE_SIZE * 0.6] as [number, number, number]}
          color="#ff00ff"
        />
      </group>

      {/* Glow light */}
      <pointLight
        position={[0, tileY + 0.35, 0]}
        color="#ff00ff"
        intensity={1.5}
        distance={2}
        decay={2}
      />
    </group>
  );
}
